import some from 'lodash/some'

import { ApiCustomer, ApiCustomerGroup } from '@vend/types'
import { PHONE_REGEX } from '../constants'
import { isValidCustomer } from './sale'

/**
 * Group ID of the All Customers group.
 */
export const ALL_CUSTOMERS_GROUP_ID = '1111111111'

/**
 * Return true if the given CustomerGroup is the All Customers group.
 *
 * @param  customerGroup {CustomerGroup}
 *         The customer group to inspect
 *
 * @return True if the given group is the All Customers group, false otherwise
 */
export const isGroupAllCustomersGroup = (customerGroup: ApiCustomerGroup) =>
  customerGroup.group_id === ALL_CUSTOMERS_GROUP_ID

const multiFieldPrefixLookup: Record<
  string,
  Array<'physical_' | 'postal_' | 'custom_field'>
> = {
  address: ['physical_', 'postal_'],
  physical_address: ['physical_'],
  postal_address: ['postal_'],
  custom_fields: ['custom_field'],
}

/**
 * Responsible for determining if a customer field has a value; physical_address, postal_address and
 * custom_fields are special cases where their appropriate range of fields are checked for values.
 *
 * @param  customer
 *         The customer which is to be evaluated
 *
 * @param  field
 *         The customer field which is to be evaluated
 *
 * @return True if the customer property has a value, false otherwise.
 */
export const checkCustomerFieldForValue = (
  customer: ApiCustomer,
  field: string
) => {
  const prefix = multiFieldPrefixLookup[field]
  if (!prefix) {
    return Boolean((customer as any)[field])
  }

  return prefix.some(lookupField =>
    some(
      customer,
      (value, key) => Boolean(value) && key.startsWith(lookupField)
    )
  )
}

/**
 * Function for getting the comma-separated address of a given Customer.
 *
 * @param  customer
 *         The customer whose address should be formatted.
 *
 * @param  addressType
 *         The type of address to format. Either physical or postal.
 *
 * @return The formatted address.
 */
export function customerAddress(
  customer: ApiCustomer,
  addressType: 'physical' | 'postal'
) {
  const addressDetails = [
    customer[`${addressType}_address_1`],
    customer[`${addressType}_address_2`],
    customer[`${addressType}_suburb`],
    customer[`${addressType}_city`],
    customer[`${addressType}_state`],
    customer[`${addressType}_postcode`],
    customer[`${addressType}_country_id`],
  ]

  return addressDetails.filter(s => s).join(', ')
}

/**
 * Prefix the given Twitter handle with the @ symbol (unless it already starts with it).
 *
 * @param  handle
 *         The Twitter handle to format
 *
 * @return The given Twitter handle prefixed with @
 */
export const twitterHandle = (handle: string) => {
  if (handle && !handle.startsWith('@')) {
    return `@${handle}`
  } else {
    return handle
  }
}

export interface CustomerFieldFromQuery {
  firstName: string
  lastName: string
  email: string
  mobile: string
}

/**
 * Detect search query to put it in proper customer fields.
 *
 * @param  query
 *         The search query from input
 *
 * @return {CustomerFieldFromQuery} Object contains formatted customer fields
 */
export const detectCustomerFieldFromQuery = (
  query: string
): CustomerFieldFromQuery => {
  const customerFields = {
    email: '',
    mobile: '',
    firstName: '',
    lastName: '',
  }
  if (query.indexOf('@') > -1) {
    customerFields.email = query
  } else if (query.match(PHONE_REGEX)) {
    customerFields.mobile = query
  } else {
    // Splits each word provided
    const customerName = query.match(/[-\w']+/g)
    if (customerName) {
      customerFields.firstName = customerName[0]
      // Add every word after the first as the last name
      if (customerName.length > 1) {
        customerName.shift()
        customerFields.lastName = customerName.join(' ')
      }
    }
  }

  return customerFields
}

/**
 * Check if the On Account (OA) limit is unlimited.
 *
 * @return {Boolean} True if OA limit is unlimited, false otherwise
 */
export const isOnAccountLimitUnlimited = (customer: ApiCustomer | null) => {
  return customer?.on_account_limit === null
}

/**
 * Check if the On Account (OA) limit is disabled.
 *
 * @return {Boolean} True if OA limit is disabled, false otherwise
 */
export const isOnAccountLimitDisabled = (customer: ApiCustomer | null) => {
  return customer?.on_account_limit === 0
}

/**
 * Return true if the sale has a valid (non-WALKIN) customer with loyalty enabled
 *
 * @return {Boolean} True if the sale has a valid customer with loyalty enabled
 */
export const hasValidCustomerWithLoyalty = (customer?: ApiCustomer) => {
  return Boolean(isValidCustomer(customer) && customer.enable_loyalty)
}
